import React, { useState, useEffect } from "react";
import axios from "axios";

const EditAddressModal = ({ isOpen, onClose, address, onUpdate }) => {
  const addressId = typeof address === "string" ? address : address?._id;
  const [formData, setFormData] = useState({
    user: "",
    fullName: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    landmark: "",
    addressType: "Home",
    isDefault: false,
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (address && typeof address === "object" && address._id) {
      console.log("Using provided address object:", address);
      setFormData((prevData) => ({
        ...prevData,
        ...address,
      }));
    }
  }, [address]);

  useEffect(() => {
    if (isOpen && addressId && (typeof address === "string" || !address)) {
      fetchAddressData();
    }
  }, [isOpen, addressId, address]);

  const fetchAddressData = async () => {
    setFetchLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/address/get-address-by-id/${addressId}`
      );

      console.log("Fetched address data:", response.data);

      const addressData =
        response.data.address || response.data.data || response.data;

      if (addressData) {
        setFormData((prev) => ({
          ...prev,
          user: addressData.user || prev.user,
          fullName: addressData.fullName || prev.fullName,
          phone: addressData.phone || prev.phone,
          addressLine1: addressData.addressLine1 || prev.addressLine1,
          addressLine2: addressData.addressLine2 || prev.addressLine2,
          city: addressData.city || prev.city,
          state: addressData.state || prev.state,
          postalCode: addressData.postalCode || prev.postalCode,
          country: addressData.country || prev.country,
          landmark: addressData.landmark || prev.landmark,
          addressType: addressData.addressType || prev.addressType,
          isDefault:
            addressData.isDefault !== undefined
              ? addressData.isDefault
              : prev.isDefault,
        }));
      } else {
        setMessage("Address data structure is not as expected");
        console.error("Unexpected data structure:", response.data);
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setMessage("Failed to load address data.");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const updateId = addressId || formData._id;
      if (!updateId) {
        throw new Error("No address ID available for update");
      }

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/address/update-address/${updateId}`,
        formData,
        { headers: { "Content-Type": "application/json" } }
      );
      setMessage("Address updated successfully!");
      console.log(response.data);
      if (onUpdate) onUpdate();
      setTimeout(() => onClose(), 1000);
    } catch (error) {
      console.error(error);
      setMessage("Failed to update address.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl"
        >
          &times;
        </button>
        <h2 className="text-2xl font-semibold mb-4">Edit Address</h2>

        {fetchLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
          >
            {[
              { label: "Full Name", name: "fullName" },
              { label: "Phone", name: "phone" },
              { label: "Address Line 1", name: "addressLine1" },
              { label: "Address Line 2", name: "addressLine2" },
              { label: "City", name: "city" },
              { label: "State", name: "state" },
              { label: "Postal Code", name: "postalCode" },
              { label: "Country", name: "country" },
              { label: "Landmark", name: "landmark" },
            ].map(({ label, name }) => (
              <div key={name}>
                <label className="block text-sm font-medium text-gray-700">
                  {label}
                </label>
                <input
                  type="text"
                  name={name}
                  value={formData[name] || ""}
                  onChange={handleChange}
                  required={name !== "addressLine2" && name !== "landmark"}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-lg"
                />
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Address Type
              </label>
              <select
                name="addressType"
                value={formData.addressType}
                onChange={handleChange}
                className="w-full mt-1 p-2 border border-gray-300 rounded-lg"
              >
                <option value="Home">Home</option>
                <option value="Work">Work</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleChange}
                className="mr-2"
              />
              <label className="text-sm">Set as default address</label>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
              disabled={loading}
            >
              {loading ? "Saving..." : "Update Address"}
            </button>

            {message && (
              <p
                className={`mt-2 text-center ${
                  message.includes("success")
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {message}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default EditAddressModal;
